import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { SlidersHorizontal, Star, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import MenuPrefsEditor from "@/components/MenuPrefsEditor";

// Ces entrées restent toujours visibles (accès au panneau).
const LOCKED = new Set(["nav-espace-entreprise"]);
const CACHE = "menu_items_cache";

export const collectItems = () => {
  const seen = new Map();
  document.querySelectorAll("[data-nav-root] a[data-testid^='nav-']").forEach((a) => {
    const id = a.getAttribute("data-testid");
    if (LOCKED.has(id) || seen.has(id)) return;
    seen.set(id, { id, label: (a.textContent || "").replace(/\d+$/, "").trim() || id, href: a.getAttribute("href") || "" });
  });
  return [...seen.values()];
};

export const mergeCache = (found) => {
  const known = new Map(JSON.parse(localStorage.getItem(CACHE) || "[]").map((i) => [i.id, i]));
  found.forEach((i) => known.set(i.id, i));
  const all = [...known.values()];
  localStorage.setItem(CACHE, JSON.stringify(all));
  return all;
};

export const MenuHiddenStyle = ({ hidden }) => {
  if (!hidden?.length) return null;
  const css = hidden.map((h) => `[data-nav-root] [data-testid="${h.replace(/"/g, "")}"]`).join(",") + "{display:none!important}";
  return <style data-testid="menu-hidden-style">{css}</style>;
};

export const FavoritesBar = ({ favorites, onClick }) => {
  if (!favorites?.length) return null;
  return (
    <div className="px-3 pt-3 shrink-0 flex flex-col max-h-[30vh] min-h-0" data-testid="menu-favorites-bar">
      <p className="px-3 pb-1 shrink-0 text-[10px] uppercase tracking-wider text-[#FFB020] flex items-center gap-1"><Star className="w-3 h-3" fill="currentColor" /> Favoris{favorites.length > 5 && <span className="ml-auto font-code text-zinc-500 normal-case">{favorites.length}</span>}</p>
      <div className="min-h-0 overflow-y-auto space-y-1 [scrollbar-width:thin]" data-testid="menu-favorites-scroll">
      {favorites.map((f) => (
        <NavLink key={f.id} to={f.href} end onClick={onClick} data-testid={`fav-${f.id}`}
          className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${isActive ? "bg-[#FFB020]/10 text-white" : "text-zinc-300 hover:bg-zinc-900 hover:text-white"}`}>
          <Star className="w-3.5 h-3.5 text-[#FFB020]" /> <span className="truncate">{f.label}</span>
        </NavLink>
      ))}
      </div>
      <div className="border-b border-white/10 pt-2 shrink-0" />
    </div>
  );
};

export const MenuCustomizeButton = ({ onOpen, count }) => (
  <button type="button" onClick={onOpen} data-testid="menu-customize-button"
    className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-xs text-zinc-500 hover:text-white hover:bg-zinc-900 transition-colors whitespace-nowrap">
    <SlidersHorizontal className="w-3.5 h-3.5 shrink-0" /> Personnaliser le menu
    {count > 0 && <span className="ml-auto text-[10px] font-code rounded-full bg-white/10 px-1.5" title="Onglets masqués" data-testid="menu-hidden-count">{count}</span>}
  </button>
);

export default function MenuCustomizer({ open, setOpen, prefs, setPrefs, onBeforeOpen, onNavigate }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (!open) return;
    onBeforeOpen?.();
    const t = setTimeout(() => setItems(mergeCache(collectItems())), 80);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [open]);

  const save = async (next) => {
    const prev = prefs;
    setPrefs({ ...next, source: "user" });
    try { await api.put("/auth/menu-prefs", { hidden: next.hidden, favorites: next.favorites }); }
    catch { setPrefs(prev); toast.error("Préférences non enregistrées"); }
  };
  const reset = async () => {
    try { const { data } = await api.delete("/auth/menu-prefs"); setPrefs(data); toast.success("Menu par défaut de votre rôle rétabli"); }
    catch { toast.error("Réinitialisation impossible"); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-[#121212] border-white/10 text-white max-w-md max-h-[85vh] overflow-y-auto" data-testid="menu-customize-panel">
        <DialogHeader>
          <DialogTitle className="font-display">Personnaliser le menu</DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">Décochez pour masquer, étoile pour mettre en favori, glissez les favoris pour les ordonner. Enregistré sur votre compte.</DialogDescription>
        </DialogHeader>
        <MenuPrefsEditor items={items} prefs={prefs} onChange={save}
          onOpenItem={(i) => { setOpen(false); onNavigate?.(); navigate(i.href); }} />
        {prefs.source === "user" && (
          <button type="button" onClick={reset} data-testid="menu-reset" className="mt-2 text-xs text-zinc-500 hover:text-white flex items-center gap-1.5">
            <RotateCcw className="w-3.5 h-3.5" /> Revenir au menu par défaut de mon rôle
          </button>
        )}
      </DialogContent>
    </Dialog>
  );
}
